import { applySnapshot, flow, types } from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import { FileModel, IFileModel } from "../../components/fileupload/FileModel";
import { IInput } from "../../utils/common-interface";
import customtypes from "../../utils/mobx-types-helper";
import { OrganizeModel, ShareHolderModel } from "../org/OrganizeModel";
import { PaginationModel } from "../share";
import {
  IPermissionListModel,
  IPermissionModel,
  PermissionModel,
} from "./PermissionModel";
import permissionAPI from "./PermissionService";
import { IRoleListModel, IRoleModel, RoleModel } from "./RoleModel";
import roleAPI from "./RoleService";
import userAPI from "./UserService";

// Helper function to convert string to Date
const DateStringToDate = (value: any) => {
  if (typeof value === 'string') {
    return new Date(value);
  }
  return value;
};

export const UserModel = types
  .model("UserModel", {
    id: types.maybeNull(types.string),
    userName: customtypes.optional(types.string, ""),
    email: customtypes.optional(types.string, ""),
    tFirstName: customtypes.optional(types.string, ""),
    tLastName: customtypes.optional(types.string, ""),
    eFirstName: customtypes.optional(types.string, ""),
    eLastName: customtypes.optional(types.string, ""),
    lastAccess: customtypes.optional(types.string, ""),
    department: customtypes.optional(types.string, ""),
    tel1: customtypes.optional(types.string, ""),
    tel2: customtypes.optional(types.string, ""),
    isActive: types.maybeNull(types.boolean),
    address: customtypes.optional(types.string, ""),
    position: customtypes.optional(types.string, ""),
    organization: customtypes.optional(OrganizeModel, {}),
    organizationShare: types.optional(ShareHolderModel, { shareHolderID: 0 }),
    phoneNumber: customtypes.optional(types.string, ""),
    phoneNumberConfirmed: customtypes.optional(types.boolean, false),
    organizationID: customtypes.optional(types.number, 443), 
    organizationCode: customtypes.optional(types.string, ""),
    organizationTHName: customtypes.optional(types.string, ""),
    error: types.optional(ErrorModel, {}),
    attachFiles: customtypes.optional(types.array(FileModel), []),
    idCardNo: customtypes.optional(types.string, ""),
    startDateTime: types.maybeNull(types.union(types.Date, types.string)),
    endDateTime : types.maybeNull(types.union(types.Date, types.string)),
    accessFailedCount: customtypes.optional(types.number, 0),
  })
  .views((self: any) => ({
    get fullname() {
      const space = self.tFirstName && self.tLastName ? " " : "";
      return `${self.tFirstName || ""}${space}${self.tLastName || ""}`;
    },
  }))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      if (fieldname === "startDateTime" || fieldname === "endDateTime") {
        self[fieldname] = DateStringToDate(value); // Convert string to Date
      } else {
        self[fieldname] = value;
      }
    },
    setAllField: (data: any) => {
      Object.keys(data).forEach((key) => {
        try {
          if (key === "startDateTime" || key === "endDateTime") {
            self[key] = DateStringToDate(data[key]); // Convert string to Date
          } else {
            self[key] = data[key];
          }
        } catch (e) {
          console.log("exception on mapping key:", key);
        }
      });
    },
    onAddUsersFile: (fieldname: "lawOfDebts" | "attachFiles", file: File[]) => {
      file.forEach((_file: File) => {
        try {
          const newFileUpload: IFileModel = FileModel.create({});
          newFileUpload.setToBase64Format(_file);
          self.onAddItem(fieldname, newFileUpload);
        } catch (e) {
          console.log("onAddDebtsFile Error: ", e);
        }
      });
    },
    onAddItem: (fieldname: string, value: any) => {
      self[fieldname].push(value);
    },
    onRemoveFile: (
      fieldname: "lawOfDebts" | "attachFiles",
      file: IFileModel,
    ) => {
      const index = self[fieldname].findIndex(
        (_file: IFileModel) => _file.fileName === file.fileName,
      );
      if (index >= 0) {
        self[fieldname].splice(index, 1);
      }
    },
    removeUser: flow(function* () {
      try {
        self.loading = true;
        const result = yield userAPI.removeUser(self.id);
        return result;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    getUser: flow(function* (userID: string) {
      try {
        const result = yield userAPI.getUser(userID);
        self.setAllField(result.data.userProfile);
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    resetAll: () => {
      applySnapshot(self, {});
    },
  }));

export const UserMainModel = types
  .model("UserMainModel", {
    userProfile: types.optional(UserModel, {}),
    roles: customtypes.optional(types.array(RoleModel), []),
    permissions: customtypes.optional(types.array(PermissionModel), []),
    error: types.optional(ErrorModel, {}),
    loading: types.optional(types.boolean, false),
  })
  .views((self: any) => ({}))
  .actions((self: any) => ({
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    setAllField: (data: any) => {
      Object.keys(data).forEach((key) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log("exception on mapping key:", key);
        }
      });
    },
    getRole: flow(function* () {
      try {
        const result = yield roleAPI.getRole();
        self.roles = result.data;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    getPermission: flow(function* () {
      try {
        const result = yield permissionAPI.getPermission();
        self.permissions = result.data;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    getUser: flow(function* (userID: string) {
      try {
        self.loading = true;
        const result = yield userAPI.getUser(userID);
        self.userProfile = result.data.userProfile;
        self.userProfile.organizationShare.shareHolderID =
          result.data.userProfile.organization.organizeID;

        // ถ้าไม่มี changeRequest ต้องกำหนดให้เป็น {}
        // ใส่เป็น Optional ไม่ได้ เพราะเป็น Type late
        if (!self.userProfile.organization.changeRequest) {
          self.userProfile.organization.changeRequest = {};
        }

        self.roles.replace(result.data.roles);
        self.permissions.replace(result.data.permissions);
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    updateUser: flow(function* () {
      try {
        self.loading = true;
        const orgId = self.userProfile.organization.organizeID.toString();
        self.userProfile.organization.loanPowerSection = orgId;
        // เช็คว่ามี changeRequest ก่อนกำหนดค่า
        if (self.userProfile.organization.changeRequest) {
          self.userProfile.organization.changeRequest.loanPowerSection = orgId;
        }
        console.log("Data sending to API:", self); 
        const dataToSend = {
          ...self,
          userProfile: {
            ...self.userProfile,
            startDateTime: self.userProfile.startDateTime ? 
              new Date(self.userProfile.startDateTime).toISOString() : null,
            endDateTime: self.userProfile.endDateTime ? 
              new Date(self.userProfile.endDateTime).toISOString() : null
          }
        };
        const result = yield userAPI.updateUser(dataToSend);
        return result;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        console.log("Error from API:", e); 
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    createUser: flow(function* () {
      try {
        self.loading = true;
        self.userProfile.organization.organizationCode = "org";
        self.userProfile.organization.organizationTHName = "org";
        const result = yield userAPI.createUser(self);
        return result;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    resetAll: () => {
      applySnapshot(self, {});
    },
  }));

export const UserListModel = types
  .model("UserListModel", {
    list: types.optional(types.array(UserModel), []),
    paging: types.optional(PaginationModel, {
      pageSize: 25,
    }),
    error: types.optional(ErrorModel, {}),
    loading: types.optional(types.boolean, false),
  })
  .views((self: any) => ({}))
  .actions((self: any) => ({
    searchUser: flow(function* () {
      try {
        self.loading = true;
        const result = yield userAPI.searchUser(self.paging);
        self.list = result.data;
        self.error.tigger = false;
        self.setPagination(result);
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    onSelectedFilter: (value: any) => {
      self.setDefaultPagination();
      self.orgTypeCode = value;
      self.searchUser();
    },
    onSearchTextFilter: (value: any) => {
      self.setDefaultPagination();
      self.paging.searchText = value;
      self.searchUser();
    },
    onSelectedPage: (pageNo: number) => {
      self.paging.pageNo = pageNo;
      self.searchUser();
    },
    setPagination: (result: any) => {
      self.paging.pageSize = result.pageSize;
      self.paging.pageNo = result.pageNo;
      self.paging.totalRow = result.totalRow;
    },
    setDefaultPagination: () => {
      self.paging.pageNo = 1;
    },
    resetAll: () => {
      self.list = [];
      self.paging = {};
    },
  }));
export type IUserModel = typeof UserModel.Type;
export type IUserListModel = typeof UserListModel.Type;
export type IUserMainModel = typeof UserMainModel.Type;

export default UserModel.create({});
